// MarketingPage.tsx — Dark theme B2B landing page
// Converted from jeptags-landing HTML with i18n, routing, mobile menu,
// industries, technology, testimonials merged from original React component.
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface MarketingPageProps {
  onGetStarted?: () => void;
  onScheduleDemo?: () => void;
}

/* ── tiny SVG icon components ─────────────────────────────── */
const Ico = {
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 2L4 6.5v5c0 5.25 8 9.5 8 9.5s8-4.25 8-9.5v-5L12 2z" />
      <circle cx="12" cy="10" r="3" opacity="0.5" />
    </svg>
  ),
  package: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
    </svg>
  ),
  truck: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="1" y="3" width="15" height="13" rx="2" /><path d="M16 8h4a2 2 0 012 2v7a2 2 0 01-2 2H6" />
      <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  ),
  barChart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 20V10" /><path d="M12 20V4" /><path d="M6 20v-6" />
    </svg>
  ),
  qr: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="4" height="4" />
    </svg>
  ),
  alert: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <path d="M12 9v4" /><circle cx="12" cy="17" r="1" />
    </svg>
  ),
  star: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.27 5.82 21 7 14.14l-5-4.87 6.91-1.01L12 2z" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M5 12l5 5L19 7" />
    </svg>
  ),
  x: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M18 6L6 18" /><path d="M6 6l12 12" />
    </svg>
  ),
  userPlus: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="8.5" cy="7" r="4" />
      <path d="M20 8v6" /><path d="M23 11h-6" />
    </svg>
  ),
  verified: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><path d="M22 4L12 14.01l-3-3" />
    </svg>
  ),
  nfc: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M6 8.32a7.43 7.43 0 010 7.36" /><path d="M9.46 6.21a11.76 11.76 0 010 11.58" />
    </svg>
  ),
  globe: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="10" /><path d="M2 12h20" />
    </svg>
  ),
  smartphone: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="5" y="2" width="14" height="20" rx="2" /><path d="M12 18h.01" />
    </svg>
  ),
  scan: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M3 7V5a2 2 0 012-2h2" /><path d="M17 3h2a2 2 0 012 2v2" />
      <path d="M21 17v2a2 2 0 01-2 2h-2" /><path d="M7 21H5a2 2 0 01-2-2v-2" /><path d="M7 12h10" />
    </svg>
  ),
  menu: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M3 12h18" /><path d="M3 6h18" /><path d="M3 18h18" />
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M18 6L6 18" /><path d="M6 6l12 12" />
    </svg>
  ),
  starFill: (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.27 5.82 21 7 14.14l-5-4.87 6.91-1.01L12 2z" />
    </svg>
  ),
};

/* ── JepTags shield logo ──────────────────────────────────── */
const Logo = ({ size = 38 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 140 140" fill="none">
    <defs>
      <linearGradient id="ng" x1="30" y1="15" x2="110" y2="125">
        <stop offset="0%" stopColor="#00FFE0" />
        <stop offset="50%" stopColor="#00E5CC" />
        <stop offset="100%" stopColor="#22D97F" />
      </linearGradient>
    </defs>
    <rect width="140" height="140" rx="30" fill="#0F2140" />
    <path d="M70 18L28 37v32c0 26 42 45 42 45s42-19 42-45V37L70 18z" fill="none" stroke="url(#ng)" strokeWidth="4" />
    <circle cx="70" cy="60" r="16" fill="none" stroke="url(#ng)" strokeWidth="2.5" opacity="0.35" />
    <text x="70" y="68" textAnchor="middle" fontFamily="Sora,sans-serif" fontWeight="800" fontSize="20" fill="url(#ng)">J</text>
  </svg>
);

/* ═══════════════════════════════════════════════════════════ */
const MarketingPage: React.FC<MarketingPageProps> = ({ onGetStarted, onScheduleDemo }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const particlesRef = useRef<HTMLDivElement>(null);

  /* scroll listener */
  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* particle generator */
  useEffect(() => {
    const el = particlesRef.current;
    if (!el) return;
    for (let i = 0; i < 30; i++) {
      const p = document.createElement('div');
      p.className = 'jt-particle';
      p.style.left = Math.random() * 100 + '%';
      p.style.animationDuration = (6 + Math.random() * 8) + 's';
      p.style.animationDelay = Math.random() * 10 + 's';
      const s = (1 + Math.random() * 2) + 'px';
      p.style.width = s;
      p.style.height = s;
      el.appendChild(p);
    }
    return () => { el.innerHTML = ''; };
  }, []);

  /* testimonial auto-rotate */
  const testimonials = [
    { name: "Sarah Chen", initials: "SC", position: t('marketing.testimonials.testimonial1.position'), company: t('marketing.testimonials.testimonial1.company'), content: t('marketing.testimonials.testimonial1.content'), color: "#3B82F6" },
    { name: "Marcus Rodriguez", initials: "MR", position: t('marketing.testimonials.testimonial2.position'), company: t('marketing.testimonials.testimonial2.company'), content: t('marketing.testimonials.testimonial2.content'), color: "#22C55E" },
    { name: "Emma Thompson", initials: "ET", position: t('marketing.testimonials.testimonial3.position'), company: t('marketing.testimonials.testimonial3.company'), content: t('marketing.testimonials.testimonial3.content'), color: "#7C3AED" },
  ];
  useEffect(() => {
    const iv = setInterval(() => setActiveTestimonial(p => (p + 1) % testimonials.length), 5000);
    return () => clearInterval(iv);
  }, [testimonials.length]);

  /* nav handlers */
  const handleGetStarted = () => (onGetStarted ? onGetStarted() : navigate('/business/register'));
  const handleScheduleDemo = () => (onScheduleDemo ? onScheduleDemo() : navigate('/business/login'));
  const handleLogin = () => navigate('/business/login');

  /* ───── RENDER ───── */
  return (
    <div className="jt-root">
      <style dangerouslySetInnerHTML={{ __html: JT_STYLES }} />

      {/* ═══ NAV ═══ */}
      <nav className={`jt-nav ${navScrolled ? 'scrolled' : ''}`}>
        <a href="#" className="nav-logo" onClick={e => { e.preventDefault(); navigate('/'); }}>
          <Logo size={38} />
          <span className="nav-wm"><span className="jep">Jep</span><span className="tags">tags</span></span>
        </a>

        {/* desktop links */}
        <div className="nav-links">
          <a href="#features">{t('marketing.nav.features')}</a>
          <a href="#how">{t('marketing.nav.pricing').includes('ricing') ? 'How It Works' : t('marketing.nav.pricing')}</a>
          <a href="#pricing">{t('marketing.nav.pricing')}</a>
          <a href="#testimonials">{t('marketing.nav.testimonials')}</a>
          <button className="nav-login" onClick={handleLogin}>{t('marketing.nav.sign_in')}</button>
          <button className="nav-cta" onClick={handleGetStarted}>{t('marketing.nav.get_started')}</button>
        </div>

        {/* mobile toggle */}
        <button className="jt-mobile-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          <span className="jt-ico-20">{mobileMenuOpen ? Ico.close : Ico.menu}</span>
        </button>

        {/* mobile drawer */}
        {mobileMenuOpen && (
          <div className="jt-mobile-menu">
            <a href="#features" onClick={() => setMobileMenuOpen(false)}>{t('marketing.nav.features')}</a>
            <a href="#how" onClick={() => setMobileMenuOpen(false)}>How It Works</a>
            <a href="#pricing" onClick={() => setMobileMenuOpen(false)}>{t('marketing.nav.pricing')}</a>
            <a href="#testimonials" onClick={() => setMobileMenuOpen(false)}>{t('marketing.nav.testimonials')}</a>
            <button className="nav-login" onClick={() => { handleLogin(); setMobileMenuOpen(false); }}>{t('marketing.nav.sign_in')}</button>
            <button className="nav-cta" onClick={() => { handleGetStarted(); setMobileMenuOpen(false); }}>{t('marketing.nav.get_started')}</button>
          </div>
        )}
      </nav>

      {/* ═══ HERO ═══ */}
      <section className="jt-hero">
        <div className="hero-grid" />
        <div className="hero-orb o1" /><div className="hero-orb o2" /><div className="hero-orb o3" />
        <div className="jt-particles" ref={particlesRef} />

        <div className="hero-inner">
          <div className="hero-text">
            <div className="hero-badge">
              <div className="badge-dot" />
              {t('marketing.hero.trusted_badge')}
            </div>

            <h1 className="hero-h">
              {t('marketing.hero.title')}{' '}
              <span className="grad">{t('marketing.hero.title_highlight')}</span>
            </h1>

            <p className="hero-p">{t('marketing.hero.description')}</p>

            <div className="hero-ctas">
              <button className="btn-p" onClick={handleGetStarted}>{t('marketing.hero.cta_trial')}</button>
              <button className="btn-o" onClick={handleScheduleDemo}>{t('marketing.hero.cta_demo')}</button>
            </div>

            <div className="trust-row">
              <div className="trust"><div className="trust-v">0.3s</div><div className="trust-l">Verify Time</div></div>
              <div className="trust"><div className="trust-v">NFC</div><div className="trust-l">Encrypted</div></div>
              <div className="trust"><div className="trust-v">24/7</div><div className="trust-l">Monitoring</div></div>
            </div>
          </div>

          {/* Dashboard Mockup */}
          <div className="hero-visual">
            <div className="dash-mock" id="demo">
              <div className="dash-frame">
                <div className="dash-bar">
                  <div className="dash-dot r" /><div className="dash-dot y" /><div className="dash-dot g" />
                  <span className="dash-url">jeptags.com/business</span>
                </div>
                <div className="dash-body">
                  <div className="mock-stats">
                    <div className="mock-stat"><div className="ms-v blue">12</div><div className="ms-l">Batches</div></div>
                    <div className="mock-stat"><div className="ms-v green">8</div><div className="ms-l">Active</div></div>
                    <div className="mock-stat"><div className="ms-v red">1</div><div className="ms-l">Recalled</div></div>
                    <div className="mock-stat"><div className="ms-v purple">4,200</div><div className="ms-l">Total Units</div></div>
                  </div>
                  <div className="mock-actions">
                    <div className="mock-action purple"><span className="ma-icon">{Ico.package}</span><div><div className="ma-text">Products</div><div className="ma-sub">Manage catalog</div></div></div>
                    <div className="mock-action green"><span className="ma-icon">{Ico.truck}</span><div><div className="ma-text">Batches</div><div className="ma-sub">Track production</div></div></div>
                    <div className="mock-action blue"><span className="ma-icon">{Ico.barChart}</span><div><div className="ma-text">Analytics</div><div className="ma-sub">Scan insights</div></div></div>
                    <div className="mock-action orange"><span className="ma-icon">{Ico.qr}</span><div><div className="ma-text">Tags</div><div className="ma-sub">NFC + QR codes</div></div></div>
                  </div>
                  <div className="mock-batch"><div className="mb-left"><div><div className="mb-id">BATCH-2024-001</div><div className="mb-name">Premium Collection</div></div></div><div className="mb-badge active">Active</div></div>
                  <div className="mock-batch"><div className="mb-left"><div><div className="mb-id">BATCH-2024-002</div><div className="mb-name">Summer Edition</div></div></div><div className="mb-badge distributed">Distributed</div></div>
                  <div className="mock-batch"><div className="mb-left"><div><div className="mb-id">BATCH-2024-003</div><div className="mb-name">Defective Line</div></div></div><div className="mb-badge recalled">Recalled</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ STATS BAR ═══ */}
      <section className="jt-stats-bar">
        {[
          { v: "5,000+", l: t('marketing.stats.brands') },
          { v: "50M+", l: t('marketing.stats.products') },
          { v: "99.8%", l: t('marketing.stats.accuracy') },
          { v: "45", l: t('marketing.stats.countries') },
        ].map((s, i) => (
          <div key={i} className="stat-item">
            <div className="stat-v">{s.v}</div>
            <div className="stat-l">{s.l}</div>
          </div>
        ))}
      </section>

      {/* ═══ PROBLEM / SOLUTION ═══ */}
      <section className="jt-problem">
        <div className="prob-grid">
          <div className="prob-col">
            <h2>Counterfeits cost brands <span style={{ color: 'var(--red)' }}>$2 trillion</span> per year</h2>
            <p>Your brand reputation, customer trust, and revenue are at risk. Without authentication, you can't tell — and neither can your customers.</p>
            <ul className="pain-list">
              <li><span className="pain-icon red">{Ico.x}</span> Fake products damage trust and cause returns</li>
              <li><span className="pain-icon red">{Ico.x}</span> No visibility into where products end up</li>
              <li><span className="pain-icon red">{Ico.x}</span> Recalls are slow and manual</li>
            </ul>
          </div>
          <div className="prob-col">
            <h2><span className="grad">JepTags</span> solves this</h2>
            <p>Encrypted NFC tags + QR codes on every product. Your customers verify instantly. You see everything from the dashboard.</p>
            <ul className="pain-list">
              <li><span className="pain-icon green">{Ico.check}</span> Every product is verifiable in 0.3 seconds</li>
              <li><span className="pain-icon green">{Ico.check}</span> Full traceability — who scanned, where, when</li>
              <li><span className="pain-icon green">{Ico.check}</span> One-click batch recalls with instant alerts</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ═══ INDUSTRIES ═══ */}
      <section className="jt-industries">
        <div className="sec-hdr">
          <h2>{t('marketing.industries.title')}</h2>
          <p>{t('marketing.industries.subtitle')}</p>
        </div>
        <div className="ind-grid">
          {[
            { emoji: "👜", title: t('marketing.industries.luxury.title'), desc: t('marketing.industries.luxury.description'), cls: 'purple' },
            { emoji: "🍕", title: t('marketing.industries.food.title'), desc: t('marketing.industries.food.description'), cls: 'green' },
            { emoji: "💊", title: t('marketing.industries.pharma.title'), desc: t('marketing.industries.pharma.description'), cls: 'red' },
            { emoji: "🚗", title: t('marketing.industries.automotive.title'), desc: t('marketing.industries.automotive.description'), cls: 'blue' },
            { emoji: "🏭", title: t('marketing.industries.manufacturing.title'), desc: t('marketing.industries.manufacturing.description'), cls: 'slate' },
            { emoji: "💎", title: t('marketing.industries.jewelry.title'), desc: t('marketing.industries.jewelry.description'), cls: 'indigo' },
          ].map((ind, i) => (
            <div key={i} className={`ind-card ${ind.cls}`}>
              <span className="ind-emoji">{ind.emoji}</span>
              <h3>{ind.title}</h3>
              <p>{ind.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ TECHNOLOGY ═══ */}
      <section className="jt-tech">
        <div className="sec-hdr">
          <h2>{t('marketing.technology.title')}</h2>
          <p>{t('marketing.technology.subtitle')}</p>
        </div>
        <div className="tech-grid">
          {[
            { icon: Ico.smartphone, title: t('marketing.technology.nfc.title'), sub: t('marketing.technology.nfc.subtitle'), cls: 'blue', keys: ['feature1','feature2','feature3','feature4'], prefix: 'marketing.technology.nfc' },
            { icon: Ico.scan, title: t('marketing.technology.rfid.title'), sub: t('marketing.technology.rfid.subtitle'), cls: 'purple', keys: ['feature1','feature2','feature3','feature4'], prefix: 'marketing.technology.rfid' },
            { icon: Ico.qr, title: t('marketing.technology.qr.title'), sub: t('marketing.technology.qr.subtitle'), cls: 'green', keys: ['feature1','feature2','feature3','feature4'], prefix: 'marketing.technology.qr' },
          ].map((tech, i) => (
            <div key={i} className={`tech-card ${tech.cls}`}>
              <div className={`tech-icon ${tech.cls}`}>{tech.icon}</div>
              <h3>{tech.title}</h3>
              <div className="tech-sub">{tech.sub}</div>
              <ul className="tech-features">
                {tech.keys.map(k => (
                  <li key={k}><span className="check-ico">{Ico.check}</span>{t(`${tech.prefix}.${k}`)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="tech-stats">
          {[
            { v: "100%", l: t('marketing.technology.stat1'), c: 'var(--blue)' },
            { v: "99.8%", l: t('marketing.technology.stat2'), c: 'var(--green)' },
            { v: "<2s", l: t('marketing.technology.stat3'), c: 'var(--purple)' },
            { v: "5000+", l: t('marketing.technology.stat4'), c: 'var(--orange)' },
          ].map((s, i) => (
            <div key={i} className="tech-stat-item">
              <div className="tech-stat-v" style={{ color: s.c }}>{s.v}</div>
              <div className="tech-stat-l">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section className="jt-features" id="features">
        <div className="sec-hdr">
          <h2>{t('marketing.features_section.title')}</h2>
          <p>{t('marketing.features_section.subtitle')}</p>
        </div>
        <div className="fg">
          {[
            { icon: Ico.package, cls: 'purple', title: t('marketing.features.multi_tech.title'), desc: t('marketing.features.multi_tech.description') },
            { icon: Ico.truck, cls: 'green', title: t('marketing.features.accuracy.title'), desc: t('marketing.features.accuracy.description') },
            { icon: Ico.barChart, cls: 'blue', title: t('marketing.features.analytics.title'), desc: t('marketing.features.analytics.description') },
            { icon: Ico.qr, cls: 'orange', title: t('marketing.features.engagement.title'), desc: t('marketing.features.engagement.description') },
            { icon: Ico.alert, cls: 'red', title: t('marketing.features.global.title'), desc: t('marketing.features.global.description') },
            { icon: Ico.star, cls: 'cyan', title: t('marketing.features.enterprise.title'), desc: t('marketing.features.enterprise.description') },
          ].map((f, i) => (
            <div key={i} className="fc">
              <div className={`fi ${f.cls}`}>{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section className="jt-how" id="how">
        <div className="sec-hdr"><h2>Get Started in <span className="grad">4 Steps</span></h2><p>From sign-up to live authentication in days, not months.</p></div>
        <div className="steps">
          {[
            { icon: Ico.userPlus, title: "Register Business", desc: "Sign up and create your business profile in minutes" },
            { icon: Ico.package, title: "Add Products", desc: "Create your product catalog and set up batches" },
            { icon: Ico.qr, title: "Apply Tags", desc: "Attach NFC chips + QR codes to your products" },
            { icon: Ico.verified, title: "Go Live", desc: "Customers verify instantly. You monitor from the dashboard." },
          ].map((step, i) => (
            <div key={i} className="step">
              <div className="step-icon">
                <div className="step-num">{i + 1}</div>
                {step.icon}
              </div>
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ CONSUMER TOOL ═══ */}
      <section className="jt-consumer">
        <div className="cons-grid">
          <div className="cons-text">
            <h2>Your customers verify with <span className="grad">one tap</span></h2>
            <p>Consumers don't need to download anything. They tap the NFC tag or scan the QR code — the web scanner or app handles the rest. Green for authentic, red for fake. Plus they can win lottery rewards with every scan.</p>
            <div className="cons-chips">
              <div className="chip nfc"><span className="chip-ico">{Ico.nfc}</span> NFC Tap</div>
              <div className="chip qr"><span className="chip-ico">{Ico.qr}</span> QR Scan</div>
              <div className="chip web"><span className="chip-ico">{Ico.globe}</span> Web Scanner</div>
            </div>

            {/* App Store Buttons */}
            <div className="app-stores">
              <a href="https://apps.apple.com/app/jeptags" target="_blank" rel="noopener noreferrer" className="store-btn apple">
                <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                <div><div className="store-sub">Download on the</div><div className="store-name">App Store</div></div>
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.jeptags" target="_blank" rel="noopener noreferrer" className="store-btn google">
                <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M3.18 23.08c-.44-.28-.68-.76-.68-1.34V2.26c0-.58.24-1.06.68-1.34l11.06 11.08L3.18 23.08zm1.48 1.2L16.02 13.1l-2.56-2.56L4.66 24.28zm15.2-10.26L17.3 12l2.56 2.02 2.78-1.58c.58-.34.58-.88 0-1.22l-2.78-1.2zm-2.34-1.48L15.18 10.2 4.66-.28l12.86 7.32 0 0z"/></svg>
                <div><div className="store-sub">GET IT ON</div><div className="store-name">Google Play</div></div>
              </a>
            </div>
          </div>
          <div className="phone-demo">
            <div className="phone"><div className="notch" />
              <div className="screen">
                <div className="phone-header">
                  <span className="phone-logo"><Logo size={24} /></span>
                  <span className="phone-brand"><span className="jep">Jep</span><span className="tags">tags</span></span>
                  <span className="phone-menu">{Ico.menu}</span>
                </div>
                <div className="ptitle">Customer View</div>
                <div className="nfc-zone">
                  <div className="nr" /><div className="nr nr2" /><div className="nr nr3" />
                  <span className="ni"><Logo size={52} /></span>
                </div>
                <div className="pst">verifying product...</div>
                <div className="pr auth">
                  <div className="ri g"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#030a14" strokeWidth="3" strokeLinecap="round"><path d="M5 12l5 5L19 7" /></svg></div>
                  <div><div className="rt g">Authentic</div><div className="rs">Tag #JT-00482A</div></div>
                </div>
                <div className="pr lot">
                  <div className="ri c"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#030a14" strokeWidth="2.5" strokeLinecap="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.27 5.82 21 7 14.14l-5-4.87 6.91-1.01L12 2z" /></svg></div>
                  <div><div className="rt c">Reward Won!</div><div className="rs">10% discount</div></div>
                </div>
                <div className="pbn">
                  <div className="pb an"><span className="pb-ico">{Ico.nfc}</span><span>NFC</span></div>
                  <div className="pb inactive"><span className="pb-ico">{Ico.qr}</span><span>QR</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ TESTIMONIALS ═══ */}
      <section className="jt-testimonials" id="testimonials">
        <div className="sec-hdr">
          <h2>{t('marketing.testimonials_section.title')}</h2>
          <p>{t('marketing.testimonials_section.subtitle')}</p>
        </div>
        <div className="test-card">
          <div className="test-stars">
            {[...Array(5)].map((_, i) => <span key={i} className="star-ico">{Ico.starFill}</span>)}
          </div>
          <blockquote>"{testimonials[activeTestimonial].content}"</blockquote>
          <div className="test-author">
            <div className="test-avatar" style={{ background: testimonials[activeTestimonial].color }}>
              {testimonials[activeTestimonial].initials}
            </div>
            <div>
              <div className="test-name">{testimonials[activeTestimonial].name}</div>
              <div className="test-pos">{testimonials[activeTestimonial].position}</div>
              <div className="test-company">{testimonials[activeTestimonial].company}</div>
            </div>
          </div>
          <div className="test-dots">
            {testimonials.map((_, i) => (
              <button key={i} className={`test-dot ${i === activeTestimonial ? 'active' : ''}`} onClick={() => setActiveTestimonial(i)} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section className="jt-pricing" id="pricing">
        <div className="sec-hdr"><h2>Simple <span className="grad">Pricing</span></h2><p>{t('marketing.pricing_section.subtitle')}</p></div>
        <div className="price-cards">
          {/* Starter */}
          <div className="pcard starter">
            <h3>{t('marketing.pricing.starter.name')}</h3>
            <div className="price">{t('marketing.pricing.starter.price')} <span>{t('marketing.pricing.starter.period')}</span></div>
            <div className="price-note">{t('marketing.pricing.starter.description')}</div>
            <ul>
              {[0,1,2,3,4,5].map(i => (
                <li key={i}><span className="check-ico sm">{Ico.check}</span> {t(`marketing.pricing.starter.features.${i}`)}</li>
              ))}
            </ul>
            <button className="btn-o full" onClick={handleGetStarted}>{t('marketing.pricing.starter.cta')}</button>
          </div>

          {/* Professional */}
          <div className="pcard pro">
            <h3>{t('marketing.pricing.professional.name')}</h3>
            <div className="price">$299 <span>/{t('marketing.pricing.professional.period')}</span></div>
            <div className="price-note">{t('marketing.pricing.professional.description')}</div>
            <ul>
              {[0,1,2,3,4,5,6,7].map(i => (
                <li key={i}><span className="check-ico sm">{Ico.check}</span> {t(`marketing.pricing.professional.features.${i}`)}</li>
              ))}
            </ul>
            <button className="btn-p full" onClick={handleGetStarted}>{t('marketing.pricing.professional.cta')}</button>
          </div>

          {/* Enterprise */}
          <div className="pcard enterprise">
            <h3>{t('marketing.pricing.enterprise.name')}</h3>
            <div className="price">{t('marketing.pricing.enterprise.price')}</div>
            <div className="price-note">{t('marketing.pricing.enterprise.description')}</div>
            <ul>
              {[0,1,2,3,4,5,6,7].map(i => (
                <li key={i}><span className="check-ico sm">{Ico.check}</span> {t(`marketing.pricing.enterprise.features.${i}`)}</li>
              ))}
            </ul>
            <button className="btn-o full" onClick={handleScheduleDemo}>{t('marketing.pricing.enterprise.cta')}</button>
          </div>
        </div>
      </section>

      {/* ═══ BLOCKCHAIN ADD-ON ═══ */}
      <section className="jt-pricing jt-blockchain-pricing" id="blockchain-pricing">
        <div className="sec-hdr">
          <h2>
            ⛓ {t('marketing.blockchain_pricing.title_prefix')}{' '}
            <span className="grad">{t('marketing.blockchain_pricing.title_highlight')}</span>{' '}
            {t('marketing.blockchain_pricing.title_suffix')}
          </h2>
          <p>
            {t('marketing.blockchain_pricing.subtitle')}
          </p>
          <p className="bc-addon-note">
            <strong>{t('marketing.blockchain_pricing.addon_note')}</strong>
          </p>
        </div>

        <div className="price-cards">
          {/* Basic Blockchain */}
          <div className="pcard">
            <h3>{t('marketing.blockchain_pricing.basic.name')}</h3>
            <div className="price">{t('marketing.blockchain_pricing.basic.price')} <span>{t('marketing.blockchain_pricing.basic.period')}</span></div>
            <div className="price-note">{t('marketing.blockchain_pricing.basic.description')}</div>
            <ul>
              {[0,1,2,3,4,5].map(i => (
                <li key={i}><span className="check-ico sm">{Ico.check}</span> {t(`marketing.blockchain_pricing.basic.features.${i}`)}</li>
              ))}
            </ul>
            <button className="btn-o full" onClick={handleGetStarted}>{t('marketing.blockchain_pricing.basic.cta')}</button>
          </div>

          {/* Premium Blockchain - HIGHLIGHTED */}
          <div className="pcard pro">
            <h3>{t('marketing.blockchain_pricing.premium.name')}</h3>
            <div className="price">{t('marketing.blockchain_pricing.premium.price')} <span>{t('marketing.blockchain_pricing.premium.period')}</span></div>
            <div className="price-note">{t('marketing.blockchain_pricing.premium.description')}</div>
            <ul>
              {[0,1,2,3,4,5].map(i => (
                <li key={i}><span className="check-ico sm">{Ico.check}</span> {t(`marketing.blockchain_pricing.premium.features.${i}`)}</li>
              ))}
            </ul>
            <button className="btn-p full" onClick={handleGetStarted}>{t('marketing.blockchain_pricing.premium.cta')}</button>
          </div>

          {/* Enterprise Blockchain */}
          <div className="pcard enterprise">
            <h3>{t('marketing.blockchain_pricing.enterprise.name')}</h3>
            <div className="price">{t('marketing.blockchain_pricing.enterprise.price')}</div>
            <div className="price-note">{t('marketing.blockchain_pricing.enterprise.description')}</div>
            <ul>
              {[0,1,2,3,4,5].map(i => (
                <li key={i}><span className="check-ico sm">{Ico.check}</span> {t(`marketing.blockchain_pricing.enterprise.features.${i}`)}</li>
              ))}
            </ul>
            <button className="btn-o full" onClick={handleScheduleDemo}>{t('marketing.blockchain_pricing.enterprise.cta')}</button>
          </div>
        </div>

        {/* Why Blockchain explainer - mobile responsive */}
        <div className="bc-why-box">
          <h3 className="bc-why-title">
            {t('marketing.blockchain_pricing.why.title')}
          </h3>
          <div className="bc-why-grid">
            <div className="bc-why-item">
              <div className="bc-why-icon">🛡️</div>
              <h4>{t('marketing.blockchain_pricing.why.tamperproof_title')}</h4>
              <p>{t('marketing.blockchain_pricing.why.tamperproof_text')}</p>
            </div>
            <div className="bc-why-item">
              <div className="bc-why-icon">🔍</div>
              <h4>{t('marketing.blockchain_pricing.why.independent_title')}</h4>
              <p>{t('marketing.blockchain_pricing.why.independent_text')}</p>
            </div>
            <div className="bc-why-item">
              <div className="bc-why-icon">📈</div>
              <h4>{t('marketing.blockchain_pricing.why.premium_title')}</h4>
              <p>{t('marketing.blockchain_pricing.why.premium_text')}</p>
            </div>
          </div>
        </div>

        {/* Mobile-responsive CSS for blockchain section */}
        <style>{`
          .jt-blockchain-pricing { padding-top: 40px; }
          .jt-blockchain-pricing .sec-hdr p { max-width: 700px; margin: 0 auto; }
          .jt-blockchain-pricing .bc-addon-note {
            margin-top: 12px;
            color: #6b7280;
            font-size: 14px;
          }

          /* "Why Blockchain" box */
          .bc-why-box {
            max-width: 1100px;
            margin: 40px auto 0;
            padding: 32px;
            background: linear-gradient(135deg, #f5f3ff 0%, #eef2ff 100%);
            border-radius: 16px;
            border: 1px solid #e0e7ff;
          }
          .bc-why-title {
            text-align: center;
            margin-bottom: 24px;
            color: #4c1d95;
            font-size: 22px;
          }
          .bc-why-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 24px;
          }
          .bc-why-icon { font-size: 32px; margin-bottom: 8px; }
          .bc-why-item h4 {
            color: #5b21b6;
            margin: 0 0 6px 0;
            font-size: 16px;
            font-weight: 600;
          }
          .bc-why-item p {
            color: #6b7280;
            font-size: 14px;
            line-height: 1.6;
            margin: 0;
          }

          /* TABLET (768px and below) */
          @media (max-width: 900px) {
            .bc-why-grid { grid-template-columns: 1fr; gap: 20px; }
            .bc-why-box { padding: 24px; margin: 30px 16px 0; }
          }

          /* MOBILE (640px and below) */
          @media (max-width: 640px) {
            .jt-blockchain-pricing { padding-top: 24px; padding-left: 16px; padding-right: 16px; }
            .jt-blockchain-pricing .sec-hdr h2 { font-size: 22px; line-height: 1.3; }
            .jt-blockchain-pricing .sec-hdr p { font-size: 14px; }
            .bc-why-title { font-size: 18px; }
            .bc-why-icon { font-size: 28px; }
            .bc-why-item h4 { font-size: 15px; }
            .bc-why-item p { font-size: 13px; }
            .bc-why-box { padding: 20px; margin: 24px 0 0; border-radius: 12px; }
          }
        `}</style>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="jt-cta" id="start">
        <div className="cta-box">
          <h2>{t('marketing.cta.title')}</h2>
          <p>{t('marketing.cta.subtitle')}</p>
          <div className="cta-btns">
            <button className="btn-p lg" onClick={handleGetStarted}>{t('marketing.cta.trial_button')}</button>
            <button className="btn-o lg" onClick={handleScheduleDemo}>{t('marketing.cta.sales_button')}</button>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="jt-footer">
        <div className="jt-footer-inner">
          <div className="footer-top">
            <div className="footer-brand">
              <div className="fb" onClick={() => navigate('/')}>
                <Logo size={28} />
                <span><span className="jep">Jep</span><span className="tags">tags</span></span>
              </div>
              <p className="footer-tagline">{t('marketing.footer.tagline')}</p>
              <p className="footer-desc">{t('marketing.footer.description')}</p>

              <div className="footer-offices">
                <div>
                  <h4>🇺🇸 {t('marketing.footer.usa_office')}</h4>
                  <p>2635 Cunningham Ave, California, CA 95148</p>
                  <p>+1-844-737-4799 · info@jeptags.com</p>
                </div>
                <div>
                  <h4>🇻🇳 {t('marketing.footer.asia_office')}</h4>
                  <p>15 Dang Thuy Tram, Hanoi, Vietnam</p>
                  <p>+84-938-681-686 · jeptagsvn@jeptags.com</p>
                </div>
              </div>
            </div>

            <div className="footer-links-group">
              <div className="footer-col">
                <h4>Resources</h4>
                <a href="#" onClick={(e) => { e.preventDefault(); navigate('/about'); }}>About Us</a>
                <a href="#" onClick={(e) => { e.preventDefault(); navigate('/blogs'); }}>Blog</a>
                <a href="#" onClick={(e) => { e.preventDefault(); navigate('/investor'); }}>Investor</a>
                <a href="#" onClick={(e) => { e.preventDefault(); navigate('/faq'); }}>FAQ</a>
                <a href="#" onClick={(e) => { e.preventDefault(); navigate('/case-studies'); }}>Case Study</a>
              </div>
              <div className="footer-col">
                <h4>{t('marketing.footer.technologies')}</h4>
                <a href="#features">{t('marketing.footer.nfc')}</a>
                <a href="#features">{t('marketing.footer.rfid')}</a>
                <a href="#features">{t('marketing.footer.qr')}</a>
                <a href="#features">{t('marketing.footer.multi_platform')}</a>
              </div>
              <div className="footer-col">
                <h4>{t('marketing.footer.industries_title')}</h4>
                <a href="#features">{t('marketing.industries.luxury.title')}</a>
                <a href="#features">{t('marketing.industries.pharma.title')}</a>
                <a href="#features">{t('marketing.industries.food.title')}</a>
                <a href="#features">{t('marketing.industries.manufacturing.title')}</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="fl">
              <a href="#" onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}>Privacy</a>
              <a href="#" onClick={(e) => { e.preventDefault(); navigate('/terms'); }}>Terms</a>
              <a href="#" onClick={(e) => { e.preventDefault(); navigate('/faq'); }}>FAQ</a>
              <a href="https://jeptags.com/business/login" target="_blank" rel="noopener noreferrer">Dashboard</a>
              <a href="#" onClick={(e) => { e.preventDefault(); navigate('/contact'); }}>Contact</a>
            </div>
            <div className="fc2">&copy; 2026 JepTags. {t('marketing.footer.rights')}</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   CSS — direct port of the HTML design plus extra sections
   ══════════════════════════════════════════════════════════════ */
const JT_STYLES = `
/* ── RESET & VARS ── */
.jt-root *,.jt-root *::before,.jt-root *::after{margin:0;padding:0;box-sizing:border-box}
.jt-root{
  --cyan:#00E5CC;--cyan-bright:#00FFE0;--teal:#22D97F;
  --navy-deep:#030a14;--navy:#0a1628;--navy-mid:#0F2140;
  --red:#EF4444;--green:#22C55E;--purple:#7C3AED;
  --blue:#3B82F6;--orange:#F59E0B;
  font-family:'Sora',sans-serif;background:var(--navy-deep);color:#e0f0f0;overflow-x:hidden;
}
.grad{background:linear-gradient(135deg,var(--cyan-bright),var(--cyan),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.sec-hdr{text-align:center;margin-bottom:64px}
.sec-hdr h2{font-size:clamp(28px,4vw,44px);font-weight:800;letter-spacing:-1px;margin-bottom:16px}
.sec-hdr p{font-size:16px;color:rgba(224,240,240,0.45);max-width:520px;margin:0 auto}

/* ── NAV ── */
.jt-nav{position:fixed;top:0;left:0;right:0;z-index:100;padding:20px 40px;display:flex;align-items:center;justify-content:space-between;backdrop-filter:blur(20px);background:rgba(3,10,20,0.6);border-bottom:1px solid rgba(0,229,204,0.06);transition:all .4s}
.jt-nav.scrolled{padding:14px 40px;background:rgba(3,10,20,0.92)}
.nav-logo{display:flex;align-items:center;gap:12px;text-decoration:none;cursor:pointer}
.nav-wm{font-size:22px;font-weight:700;letter-spacing:-.5px}
.jep{color:var(--cyan)}.tags{color:var(--teal)}
.nav-links{display:flex;gap:20px;align-items:center}
.nav-links a{color:rgba(224,240,240,0.6);text-decoration:none;font-size:14px;font-weight:500;transition:color .3s;background:none;border:none;cursor:pointer}
.nav-links a:hover{color:var(--cyan)}
.nav-login{background:none;border:none;color:rgba(224,240,240,0.6);font-family:'Sora',sans-serif;font-size:14px;font-weight:500;cursor:pointer;transition:color .3s}
.nav-login:hover{color:var(--cyan)}
.nav-cta{padding:10px 22px;border-radius:8px;background:linear-gradient(135deg,var(--cyan-bright),var(--teal));color:var(--navy-deep)!important;font-weight:700;font-size:13px;border:none;cursor:pointer;font-family:'Sora',sans-serif;transition:transform .3s,box-shadow .3s}
.nav-cta:hover{transform:translateY(-1px);box-shadow:0 8px 30px rgba(0,229,204,0.3)}
.jt-mobile-toggle{display:none;background:none;border:none;color:var(--cyan);cursor:pointer}
.jt-ico-20 svg{width:20px;height:20px}
.jt-mobile-menu{display:none}

/* ── HERO ── */
.jt-hero{position:relative;min-height:100vh;display:flex;align-items:center;overflow:hidden}
.hero-grid{position:absolute;inset:0;background-image:linear-gradient(rgba(0,229,204,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(0,229,204,0.03) 1px,transparent 1px);background-size:60px 60px;mask-image:radial-gradient(ellipse 70% 60% at 50% 50%,black,transparent)}
.hero-orb{position:absolute;border-radius:50%;filter:blur(100px);opacity:.15;animation:orbF 12s ease-in-out infinite}
.o1{width:600px;height:600px;background:var(--cyan);top:-10%;left:-5%}
.o2{width:500px;height:500px;background:var(--teal);bottom:-15%;right:-10%;animation-delay:-4s}
.o3{width:300px;height:300px;background:#0066ff;top:30%;right:20%;animation-delay:-8s;opacity:.08}
@keyframes orbF{0%,100%{transform:translate(0,0) scale(1)}33%{transform:translate(30px,-20px) scale(1.05)}66%{transform:translate(-20px,15px) scale(.95)}}
.jt-particles{position:absolute;inset:0;overflow:hidden}
.jt-particle{position:absolute;width:2px;height:2px;background:var(--cyan);border-radius:50%;opacity:0;animation:pR linear infinite}
@keyframes pR{0%{transform:translateY(100vh) scale(0);opacity:0}10%{opacity:.6}90%{opacity:.6}100%{transform:translateY(-10vh) scale(1);opacity:0}}

.hero-inner{position:relative;z-index:10;display:flex;align-items:center;justify-content:space-between;max-width:1200px;margin:0 auto;padding:140px 40px 100px;gap:60px;width:100%}
.hero-text{flex:1;max-width:560px}
.hero-visual{flex:1;display:flex;justify-content:center}
.hero-badge{display:inline-flex;align-items:center;gap:8px;padding:8px 18px;border-radius:100px;background:rgba(0,229,204,0.08);border:1px solid rgba(0,229,204,0.15);font-size:11px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:var(--cyan);margin-bottom:24px;animation:fadeUp 1s ease-out .1s both}
.badge-dot{width:6px;height:6px;border-radius:50%;background:var(--teal);animation:dotP 2s ease-in-out infinite}
@keyframes dotP{0%,100%{opacity:1}50%{opacity:.4}}
.hero-h{font-size:clamp(36px,5vw,62px);font-weight:800;line-height:1.08;letter-spacing:-2px;margin-bottom:24px;animation:fadeUp 1s ease-out .3s both}
.hero-p{font-size:18px;font-weight:300;line-height:1.7;color:rgba(224,240,240,0.55);margin-bottom:32px;animation:fadeUp 1s ease-out .5s both}
.hero-ctas{display:flex;gap:16px;flex-wrap:wrap;animation:fadeUp 1s ease-out .7s both}
@keyframes fadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}

/* Buttons */
.btn-p{padding:16px 32px;border:none;border-radius:12px;background:linear-gradient(135deg,var(--cyan-bright),var(--teal));color:var(--navy-deep);font-family:'Sora',sans-serif;font-size:16px;font-weight:700;cursor:pointer;transition:transform .3s,box-shadow .3s;display:inline-flex;align-items:center;gap:8px;text-decoration:none}
.btn-p:hover{transform:translateY(-2px);box-shadow:0 12px 40px rgba(0,229,204,0.35)}
.btn-p.lg{font-size:17px;padding:18px 44px}
.btn-o{padding:16px 32px;border:1.5px solid rgba(0,229,204,0.25);border-radius:12px;background:rgba(0,229,204,0.04);color:var(--cyan);font-family:'Sora',sans-serif;font-size:16px;font-weight:600;cursor:pointer;transition:all .3s;display:inline-flex;align-items:center;gap:8px;text-decoration:none}
.btn-o:hover{border-color:var(--cyan);background:rgba(0,229,204,0.08)}
.btn-o.lg{font-size:17px;padding:18px 44px}
.btn-p.full,.btn-o.full{width:100%;justify-content:center;padding:14px}

.trust-row{display:flex;gap:32px;margin-top:36px;animation:fadeUp 1s ease-out .9s both}
.trust{display:flex;flex-direction:column;gap:2px}
.trust-v{font-size:24px;font-weight:800;font-family:'JetBrains Mono',monospace;background:linear-gradient(135deg,var(--cyan-bright),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.trust-l{font-size:11px;font-weight:500;letter-spacing:1px;text-transform:uppercase;color:rgba(224,240,240,0.35)}

/* ── Dashboard Mockup ── */
.dash-mock{width:480px;animation:fadeUp 1.2s ease-out .4s both}
.dash-frame{background:var(--navy);border:1.5px solid rgba(0,229,204,0.08);border-radius:16px;overflow:hidden;box-shadow:0 40px 80px rgba(0,0,0,0.5),0 0 60px rgba(0,229,204,0.06)}
.dash-bar{height:36px;background:var(--navy-mid);display:flex;align-items:center;padding:0 14px;gap:6px;border-bottom:1px solid rgba(0,229,204,0.06)}
.dash-dot{width:8px;height:8px;border-radius:50%}.dash-dot.r{background:#EF4444}.dash-dot.y{background:#F59E0B}.dash-dot.g{background:#22C55E}
.dash-url{font-family:'JetBrains Mono',monospace;font-size:10px;color:rgba(224,240,240,0.3);margin-left:12px}
.dash-body{padding:20px}
.mock-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:16px}
.mock-stat{background:rgba(0,229,204,0.04);border:1px solid rgba(0,229,204,0.08);border-radius:10px;padding:12px 10px;text-align:center}
.ms-v{font-size:18px;font-weight:800;font-family:'JetBrains Mono',monospace}
.ms-v.blue{color:var(--blue)}.ms-v.green{color:var(--green)}.ms-v.red{color:var(--red)}.ms-v.purple{color:var(--purple)}
.ms-l{font-size:8px;font-weight:600;letter-spacing:.5px;text-transform:uppercase;color:rgba(224,240,240,0.35);margin-top:4px}
.mock-actions{display:grid;grid-template-columns:repeat(2,1fr);gap:8px;margin-bottom:16px}
.mock-action{border-radius:10px;padding:14px 12px;display:flex;align-items:center;gap:10px;color:white}
.mock-action.purple{background:linear-gradient(135deg,#7C3AED,#6D28D9)}
.mock-action.green{background:linear-gradient(135deg,#22C55E,#16A34A)}
.mock-action.blue{background:linear-gradient(135deg,#3B82F6,#2563EB)}
.mock-action.orange{background:linear-gradient(135deg,#F59E0B,#D97706)}
.ma-icon{width:28px;height:28px;opacity:.9;display:flex;align-items:center;justify-content:center}
.ma-icon svg{width:28px;height:28px}
.ma-text{font-size:11px;font-weight:600}.ma-sub{font-size:8px;opacity:.7;margin-top:2px}
.mock-batch{background:rgba(0,229,204,0.03);border:1px solid rgba(0,229,204,0.06);border-radius:10px;padding:12px;display:flex;align-items:center;justify-content:space-between;margin-bottom:6px}
.mb-left{display:flex;align-items:center;gap:10px}
.mb-id{font-family:'JetBrains Mono',monospace;font-size:10px;color:var(--cyan)}
.mb-name{font-size:11px;font-weight:600;color:#e0f0f0}
.mb-badge{font-size:8px;font-weight:700;padding:3px 8px;border-radius:6px;letter-spacing:.3px;text-transform:uppercase}
.mb-badge.active{background:rgba(59,130,246,0.15);color:var(--blue);border:1px solid rgba(59,130,246,0.2)}
.mb-badge.distributed{background:rgba(34,197,94,0.15);color:var(--green);border:1px solid rgba(34,197,94,0.2)}
.mb-badge.recalled{background:rgba(239,68,68,0.15);color:var(--red);border:1px solid rgba(239,68,68,0.2)}

/* ── STATS BAR ── */
.jt-stats-bar{display:flex;justify-content:center;gap:48px;padding:60px 24px;flex-wrap:wrap;border-bottom:1px solid rgba(0,229,204,0.05)}
.stat-item{text-align:center}
.stat-v{font-size:28px;font-weight:800;font-family:'JetBrains Mono',monospace;background:linear-gradient(135deg,var(--cyan-bright),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.stat-l{font-size:11px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:rgba(224,240,240,0.3);margin-top:4px}

/* ── PROBLEM/SOLUTION ── */
.jt-problem{padding:100px 24px;max-width:1100px;margin:0 auto}
.prob-grid{display:grid;grid-template-columns:1fr 1fr;gap:48px;align-items:start}
.prob-col h2{font-size:clamp(28px,4vw,44px);font-weight:800;letter-spacing:-1px;margin-bottom:20px;line-height:1.15}
.prob-col p{font-size:16px;line-height:1.7;color:rgba(224,240,240,0.5);margin-bottom:24px}
.pain-list{list-style:none;display:flex;flex-direction:column;gap:14px}
.pain-list li{display:flex;align-items:flex-start;gap:12px;font-size:14px;color:rgba(224,240,240,0.6);line-height:1.5}
.pain-icon{width:20px;height:20px;flex-shrink:0;margin-top:2px;display:flex}
.pain-icon svg{width:20px;height:20px}
.pain-icon.red{color:var(--red)}.pain-icon.green{color:var(--green)}

/* ── INDUSTRIES ── */
.jt-industries{padding:100px 24px;max-width:1100px;margin:0 auto}
.ind-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}
.ind-card{text-align:center;padding:36px 24px;border-radius:20px;background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06);transition:all .4s;position:relative;overflow:hidden}
.ind-card:hover{border-color:rgba(0,229,204,0.15);transform:translateY(-4px);box-shadow:0 20px 60px rgba(0,0,0,0.3)}
.ind-emoji{font-size:36px;display:block;margin-bottom:16px}
.ind-card h3{font-size:17px;font-weight:700;margin-bottom:8px}
.ind-card p{font-size:13px;line-height:1.6;color:rgba(224,240,240,0.45)}
.ind-card.purple:hover{border-color:rgba(124,58,237,0.3)}.ind-card.green:hover{border-color:rgba(34,197,94,0.3)}
.ind-card.red:hover{border-color:rgba(239,68,68,0.3)}.ind-card.blue:hover{border-color:rgba(59,130,246,0.3)}
.ind-card.slate:hover{border-color:rgba(148,163,184,0.3)}.ind-card.indigo:hover{border-color:rgba(99,102,241,0.3)}

/* ── TECHNOLOGY ── */
.jt-tech{padding:100px 24px;max-width:1100px;margin:0 auto}
.tech-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:24px}
.tech-card{padding:36px;border-radius:20px;background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06);transition:all .4s;text-align:center}
.tech-card:hover{border-color:rgba(0,229,204,0.15);transform:translateY(-4px);box-shadow:0 20px 60px rgba(0,0,0,0.3)}
.tech-icon{width:56px;height:56px;border-radius:16px;display:flex;align-items:center;justify-content:center;margin:0 auto 16px}
.tech-icon svg{width:28px;height:28px}
.tech-icon.blue{background:rgba(59,130,246,0.15);color:var(--blue)}
.tech-icon.purple{background:rgba(124,58,237,0.15);color:var(--purple)}
.tech-icon.green{background:rgba(34,197,94,0.15);color:var(--green)}
.tech-card h3{font-size:20px;font-weight:700;margin-bottom:4px}
.tech-sub{font-size:13px;color:rgba(0,229,204,0.7);font-weight:600;margin-bottom:20px}
.tech-features{list-style:none;text-align:left;display:flex;flex-direction:column;gap:10px}
.tech-features li{display:flex;align-items:flex-start;gap:10px;font-size:13px;color:rgba(224,240,240,0.6);line-height:1.5}
.check-ico{width:16px;height:16px;flex-shrink:0;color:var(--teal);margin-top:1px;display:flex}
.check-ico svg{width:16px;height:16px}
.check-ico.sm svg{width:16px;height:16px}
.tech-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:24px;margin-top:48px}
.tech-stat-item{text-align:center}
.tech-stat-v{font-size:28px;font-weight:800;font-family:'JetBrains Mono',monospace}
.tech-stat-l{font-size:11px;color:rgba(224,240,240,0.35);margin-top:4px}

/* ── FEATURES ── */
.jt-features{padding:100px 24px;max-width:1100px;margin:0 auto}
.fg{display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:24px}
.fc{padding:36px;border-radius:20px;background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06);transition:all .4s;position:relative;overflow:hidden}
.fc::before{content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(0,229,204,0.2),transparent);opacity:0;transition:opacity .4s}
.fc:hover{border-color:rgba(0,229,204,0.15);transform:translateY(-4px);box-shadow:0 20px 60px rgba(0,0,0,0.3)}
.fc:hover::before{opacity:1}
.fi{width:48px;height:48px;border-radius:12px;display:flex;align-items:center;justify-content:center;margin-bottom:20px}
.fi svg{width:24px;height:24px}
.fi.purple{background:rgba(124,58,237,0.12);color:var(--purple)}
.fi.green{background:rgba(34,197,94,0.12);color:var(--green)}
.fi.blue{background:rgba(59,130,246,0.12);color:var(--blue)}
.fi.orange{background:rgba(245,158,11,0.12);color:var(--orange)}
.fi.cyan{background:rgba(0,229,204,0.08);color:var(--cyan)}
.fi.red{background:rgba(239,68,68,0.12);color:var(--red)}
.fc h3{font-size:18px;font-weight:700;margin-bottom:10px}
.fc p{font-size:14px;line-height:1.7;color:rgba(224,240,240,0.45)}

/* ── HOW IT WORKS ── */
.jt-how{padding:100px 24px;max-width:1000px;margin:0 auto}
.steps{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;position:relative}
.steps::before{content:'';position:absolute;top:48px;left:13%;right:13%;height:2px;background:linear-gradient(90deg,var(--cyan),var(--teal),var(--green),var(--cyan));opacity:.15}
.step{display:flex;flex-direction:column;align-items:center;text-align:center;gap:14px}
.step-icon{width:72px;height:72px;border-radius:20px;display:flex;align-items:center;justify-content:center;border:1.5px solid rgba(0,229,204,0.15);background:rgba(0,229,204,0.04);position:relative;z-index:2;transition:all .4s}
.step:hover .step-icon{border-color:rgba(0,229,204,0.3);box-shadow:0 8px 30px rgba(0,229,204,0.1);transform:translateY(-4px)}
.step-icon svg{width:32px;height:32px;color:var(--cyan)}
.step-num{position:absolute;top:-8px;right:-8px;width:24px;height:24px;border-radius:50%;background:var(--navy-mid);border:1.5px solid rgba(0,229,204,0.2);display:flex;align-items:center;justify-content:center;font-family:'JetBrains Mono',monospace;font-size:11px;font-weight:600;color:var(--cyan);z-index:3}
.step h3{font-size:14px;font-weight:700}
.step p{font-size:12px;line-height:1.6;color:rgba(224,240,240,0.4);padding:0 4px}

/* ── CONSUMER TOOL ── */
.jt-consumer{padding:100px 24px;max-width:1100px;margin:0 auto}
.cons-grid{display:grid;grid-template-columns:1fr 1fr;gap:60px;align-items:center}
.cons-text h2{font-size:clamp(28px,4vw,40px);font-weight:800;letter-spacing:-1px;margin-bottom:16px;line-height:1.15}
.cons-text p{font-size:16px;line-height:1.7;color:rgba(224,240,240,0.5);margin-bottom:24px}
.cons-chips{display:flex;gap:12px;flex-wrap:wrap}
.chip{display:flex;align-items:center;gap:6px;padding:8px 16px;border-radius:10px;font-size:13px;font-weight:600}
.chip-ico{width:16px;height:16px;display:flex}.chip-ico svg{width:16px;height:16px}
.chip.nfc{background:rgba(0,229,204,0.06);border:1px solid rgba(0,229,204,0.12);color:var(--cyan)}
.chip.qr{background:rgba(34,217,127,0.06);border:1px solid rgba(34,217,127,0.12);color:var(--teal)}
.chip.web{background:rgba(59,130,246,0.06);border:1px solid rgba(59,130,246,0.12);color:var(--blue)}

/* App Store Buttons */
.app-stores{display:flex;gap:12px;margin-top:20px;flex-wrap:wrap}
.store-btn{display:flex;align-items:center;gap:10px;padding:10px 20px;border-radius:12px;text-decoration:none;transition:all .3s;border:1.5px solid rgba(224,240,240,0.15);background:rgba(224,240,240,0.04)}
.store-btn:hover{border-color:var(--cyan);background:rgba(0,229,204,0.06);transform:translateY(-2px)}
.store-btn svg{width:24px;height:24px;color:#e0f0f0;flex-shrink:0}
.store-sub{font-size:9px;font-weight:500;letter-spacing:.5px;text-transform:uppercase;color:rgba(224,240,240,0.5);line-height:1}
.store-name{font-size:16px;font-weight:700;color:#e0f0f0;line-height:1.2}

/* Phone mockup */
.phone-demo{display:flex;justify-content:center}
.phone{position:relative;width:300px;height:600px;border-radius:36px;background:var(--navy);border:2px solid rgba(0,229,204,0.08);overflow:hidden;box-shadow:0 40px 80px rgba(0,0,0,0.5);animation:pFloat 6s ease-in-out infinite}
@keyframes pFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
.notch{position:absolute;top:0;left:50%;transform:translateX(-50%);width:120px;height:26px;background:var(--navy-deep);border-radius:0 0 16px 16px;z-index:5}
.screen{padding:44px 20px 16px;height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;background:linear-gradient(180deg,var(--navy-deep),var(--navy))}
.phone-header{width:100%;display:flex;align-items:center;gap:8px;padding:0 4px;margin-bottom:4px}
.phone-logo{width:24px;height:24px;display:flex;align-items:center}.phone-logo svg{width:24px;height:24px}
.phone-brand{font-size:16px;font-weight:700;flex:1}
.phone-menu{width:18px;height:18px;color:rgba(224,240,240,0.4);display:flex}.phone-menu svg{width:18px;height:18px}
.ptitle{font-size:12px;font-weight:600;color:rgba(224,240,240,0.4);letter-spacing:2.5px;text-transform:uppercase}
.nfc-zone{position:relative;width:160px;height:160px;display:flex;align-items:center;justify-content:center}
.nr{position:absolute;width:100%;height:100%;border:2px solid var(--cyan);border-radius:50%;opacity:0;animation:nrP 2.5s ease-out infinite}
.nr.nr2{animation-delay:.8s}.nr.nr3{animation-delay:1.6s}
@keyframes nrP{0%{transform:scale(.5);opacity:.6}100%{transform:scale(1.3);opacity:0}}
.ni{width:56px;height:56px;animation:nBob 2s ease-in-out infinite;display:flex;align-items:center;justify-content:center}
.ni svg{width:52px;height:52px}
@keyframes nBob{0%,100%{transform:scale(1)}50%{transform:scale(1.06)}}
.pst{font-family:'JetBrains Mono',monospace;font-size:12px;color:var(--cyan)}
.pr{width:100%;padding:14px;border-radius:12px;display:flex;align-items:center;gap:10px}
.pr.auth{background:rgba(34,197,94,0.1);border:1px solid rgba(34,197,94,0.25)}
.pr.lot{background:rgba(0,229,204,0.06);border:1px solid rgba(0,229,204,0.15)}
.ri{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.ri.g{background:var(--green)}.ri.c{background:var(--cyan)}
.rt{font-size:14px;font-weight:600}.rt.g{color:var(--green)}.rt.c{color:var(--cyan)}
.rs{font-size:11px;color:rgba(224,240,240,0.35);margin-top:2px}
.pbn{width:100%;display:flex;border-top:1px solid rgba(0,229,204,0.1);margin-top:auto}
.pb{flex:1;padding:10px 0 6px;display:flex;flex-direction:column;align-items:center;gap:4px}
.pb-ico{width:22px;height:22px;display:flex}.pb-ico svg{width:22px;height:22px}
.pb span{font-size:10px;font-weight:600;letter-spacing:.5px;text-transform:uppercase}
.pb.an{color:var(--cyan)}.pb.inactive{color:rgba(224,240,240,0.25)}

/* ── TESTIMONIALS ── */
.jt-testimonials{padding:100px 24px;max-width:800px;margin:0 auto}
.test-card{background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.08);border-radius:24px;padding:48px;text-align:center}
.test-stars{display:flex;justify-content:center;gap:4px;margin-bottom:24px}
.star-ico{width:20px;height:20px;color:#F59E0B;display:flex}.star-ico svg{width:20px;height:20px}
.test-card blockquote{font-size:18px;font-weight:400;line-height:1.8;color:rgba(224,240,240,0.7);font-style:italic;margin-bottom:32px}
.test-author{display:flex;align-items:center;justify-content:center;gap:14px}
.test-avatar{width:48px;height:48px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:14px;flex-shrink:0}
.test-name{font-size:14px;font-weight:700;text-align:left}
.test-pos{font-size:12px;color:rgba(224,240,240,0.4);text-align:left}
.test-company{font-size:12px;color:var(--cyan);font-weight:600;text-align:left}
.test-dots{display:flex;justify-content:center;gap:8px;margin-top:28px}
.test-dot{width:10px;height:10px;border-radius:50%;border:none;background:rgba(224,240,240,0.15);cursor:pointer;padding:0;transition:all .3s}
.test-dot.active{background:var(--cyan);width:28px;border-radius:5px}

/* ── PRICING ── */
.jt-pricing{padding:100px 24px;max-width:1100px;margin:0 auto}
.price-cards{display:grid;grid-template-columns:1fr 1fr 1fr;gap:24px}
.pcard{padding:40px 32px;border-radius:24px;position:relative;display:flex;flex-direction:column}
.pcard.starter{background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06)}
.pcard.pro{background:linear-gradient(135deg,rgba(0,229,204,0.08),rgba(34,217,127,0.05));border:1.5px solid rgba(0,229,204,0.15)}
.pcard.pro::before{content:'POPULAR';position:absolute;top:16px;right:16px;font-size:9px;font-weight:700;letter-spacing:1px;padding:4px 10px;border-radius:6px;background:linear-gradient(135deg,var(--cyan-bright),var(--teal));color:var(--navy-deep)}
.pcard.enterprise{background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06)}
.pcard h3{font-size:20px;font-weight:700;margin-bottom:8px}
.pcard .price{font-size:40px;font-weight:800;font-family:'JetBrains Mono',monospace;margin-bottom:4px}
.pcard .price span{font-size:16px;font-weight:400;color:rgba(224,240,240,0.4)}
.pcard .price-note{font-size:13px;color:rgba(224,240,240,0.4);margin-bottom:24px}
.pcard ul{list-style:none;margin-bottom:32px;flex:1}
.pcard ul li{font-size:13px;color:rgba(224,240,240,0.6);padding:8px 0;border-bottom:1px solid rgba(0,229,204,0.04);display:flex;align-items:center;gap:8px}

/* ── CTA ── */
.jt-cta{padding:80px 24px 120px;text-align:center}
.cta-box{max-width:700px;margin:0 auto;padding:64px 48px;border-radius:28px;background:linear-gradient(135deg,rgba(0,229,204,0.06),rgba(34,217,127,0.04));border:1px solid rgba(0,229,204,0.1);position:relative;overflow:hidden}
.cta-box::before{content:'';position:absolute;top:-50%;left:-50%;width:200%;height:200%;background:radial-gradient(circle at 30% 40%,rgba(0,229,204,0.05),transparent 50%)}
.cta-box h2{font-size:clamp(28px,4vw,40px);font-weight:800;letter-spacing:-1px;margin-bottom:16px;position:relative}
.cta-box p{font-size:16px;color:rgba(224,240,240,0.5);margin-bottom:32px;position:relative}
.cta-btns{display:flex;gap:16px;justify-content:center;flex-wrap:wrap;position:relative}

/* ── FOOTER ── */
.jt-footer{border-top:1px solid rgba(0,229,204,0.06);padding:60px 40px 40px}
.jt-footer-inner{max-width:1100px;margin:0 auto}
.footer-top{display:grid;grid-template-columns:1.5fr 2fr;gap:48px;margin-bottom:48px}
.footer-brand{display:flex;flex-direction:column;gap:8px}
.fb{display:flex;align-items:center;gap:10px;cursor:pointer}
.fb span{font-weight:700;font-size:16px}
.footer-tagline{font-size:11px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:rgba(224,240,240,0.25)}
.footer-desc{font-size:13px;color:rgba(224,240,240,0.4);line-height:1.6;max-width:320px}
.footer-offices{display:flex;flex-direction:column;gap:16px;margin-top:16px}
.footer-offices h4{font-size:12px;font-weight:700;margin-bottom:4px}
.footer-offices p{font-size:11px;color:rgba(224,240,240,0.35);line-height:1.5}
.footer-links-group{display:grid;grid-template-columns:1fr 1fr 1fr;gap:24px}
.footer-col{display:flex;flex-direction:column;gap:10px}
.footer-col h4{font-size:12px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:rgba(224,240,240,0.5);margin-bottom:4px}
.footer-col a{font-size:13px;color:rgba(224,240,240,0.35);text-decoration:none;transition:color .3s}
.footer-col a:hover{color:var(--cyan)}
.footer-bottom{border-top:1px solid rgba(0,229,204,0.06);padding-top:24px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:16px}
.fl{display:flex;gap:24px}
.fl a{font-size:13px;color:rgba(224,240,240,0.35);text-decoration:none;transition:color .3s}
.fl a:hover{color:var(--cyan)}
.fc2{font-size:12px;color:rgba(224,240,240,0.2)}

/* ── RESPONSIVE ── */
@media(max-width:900px){
  .hero-inner{flex-direction:column;text-align:center;padding:120px 24px 60px}
  .hero-text{max-width:100%}.hero-ctas{justify-content:center}.trust-row{justify-content:center}
  .dash-mock{width:100%;max-width:420px}
  .prob-grid,.cons-grid{grid-template-columns:1fr;text-align:center}
  .pain-list{align-items:center}.cons-chips{justify-content:center}.app-stores{justify-content:center}
  .steps{grid-template-columns:1fr 1fr;gap:24px}.steps::before{display:none}
  .price-cards{grid-template-columns:1fr}
  .ind-grid{grid-template-columns:1fr 1fr}
  .tech-grid{grid-template-columns:1fr}
  .tech-stats{grid-template-columns:repeat(2,1fr)}
  .jt-nav{padding:14px 20px}
  .nav-links{display:none}
  .jt-mobile-toggle{display:block}
  .jt-mobile-menu{display:flex;flex-direction:column;position:absolute;top:100%;left:0;right:0;background:rgba(3,10,20,0.97);backdrop-filter:blur(20px);border-bottom:1px solid rgba(0,229,204,0.1);padding:16px 24px;gap:8px}
  .jt-mobile-menu a,.jt-mobile-menu button{display:block;width:100%;text-align:left;color:rgba(224,240,240,0.6);text-decoration:none;font-size:15px;font-weight:500;padding:12px 8px;border-radius:8px;transition:all .3s;background:none;border:none;font-family:'Sora',sans-serif;cursor:pointer}
  .jt-mobile-menu a:hover,.jt-mobile-menu button:hover{color:var(--cyan);background:rgba(0,229,204,0.04)}
  .jt-mobile-menu .nav-cta{text-align:center;margin-top:8px;color:var(--navy-deep)!important;padding:12px}
  .footer-top{grid-template-columns:1fr}
  .footer-links-group{grid-template-columns:1fr 1fr 1fr}
  .footer-bottom{flex-direction:column;text-align:center}.fl{justify-content:center}
}
@media(max-width:600px){
  .jt-stats-bar{gap:24px}
  .ind-grid{grid-template-columns:1fr}
  .steps{grid-template-columns:1fr}
  .mock-stats{grid-template-columns:repeat(2,1fr)}.mock-actions{grid-template-columns:1fr}
  .test-card{padding:32px 24px}
  .footer-links-group{grid-template-columns:1fr}
}
`;

export default MarketingPage;